import { useState } from 'react';
import type { EventReadinessState } from '../console';
import {
  PHASE_MEANING,
  type PhaseReadiness,
  type ReadinessTab,
} from '../readiness';

/**
 * TICK-135 — the readiness strip the approved mockup puts at the top of the Command Center, now
 * that TICK-120 §F-9 has an answer. `readiness.ts` decides what the numbers mean, `useEventReadiness`
 * does the reading, and this file only draws them. TICK-148: the state is now passed in rather than
 * read here, because the action list below needs the same answer.
 *
 * The twelve reads and the 403-is-data rule moved into that hook when the v3 Command Center was
 * built, so both shells ask the same question of the same endpoints. What stays true here either
 * way: the strip loads on its own, because waiting for twelve reads before showing "there are four
 * slips older than fifteen minutes" would make the screen's most urgent half wait on its slowest.
 */

const STATE_LABEL: Record<PhaseReadiness['state'], string> = {
  ready: 'พร้อม',
  not_ready: 'ยังไม่พร้อม',
  partial_view: 'ดูไม่ครบ',
  no_access: 'ไม่มีสิทธิ์',
};

export function ReadinessSection({ state, onGoTo }: {
  /** Read once by `CommandCenterHome`, so this strip and the action list beside it share one answer. */
  state: EventReadinessState;
  onGoTo: (tab: ReadinessTab) => void;
}) {
  const { readiness, error } = state;
  const [open, setOpen] = useState<string | null>(null);

  if (error) {
    return (
      <section className="readiness">
        <h3>ความพร้อมของงาน</h3>
        <div className="form-error">{error}</div>
      </section>
    );
  }

  if (!readiness) {
    return (
      <section className="readiness readiness--loading" aria-busy="true">
        <h3>ความพร้อมของงาน</h3>
        <p className="section-note">กำลังอ่าน…</p>
      </section>
    );
  }

  const visible = readiness.phases.filter((p) => p.state !== 'no_access');

  return (
    <section className="readiness">
      <h3 style={{ marginBottom: 4 }}>ความพร้อมของงาน</h3>
      {/*
        * Said in one line rather than left to be inferred, because the difference between the badge
        * and the bar is the whole of TICK-135 rule 3: a phase is ready when the things that block
        * it pass, which is not the same question as how much of its checklist is filled in.
        */}
      <p className="section-note">
        นับจากสิ่งที่ต้องตั้งค่าไว้ล่วงหน้า ไม่ใช่คิวงานตอนนี้ ·
        “พร้อม” หมายถึงข้อที่กันไม่ให้เดินต่อผ่านครบแล้ว ส่วนเปอร์เซ็นต์คือทั้งรายการ
      </p>

      <div className="readiness-head">
        <div className="readiness-figure">
          <span className="pct">{readiness.percent === null ? '—' : `${readiness.percent}%`}</span>
          <span className="of">
            {readiness.applicable === 0
              ? 'ไม่มีข้อที่อ่านได้'
              : `${readiness.passed}/${readiness.applicable} ข้อ`}
          </span>
        </div>
        <div className="readiness-summary">
          <strong>พร้อม {readiness.readyPhases}/{readiness.visiblePhases} เฟส</strong>
          {readiness.partial && (
            <span className="readiness-partial">
              บางเฟสมีข้อที่ Token นี้อ่านไม่ได้ — ตัวเลขจึงเป็นของเท่าที่เห็น
            </span>
          )}
        </div>
      </div>

      <div className="readiness-grid">
        {visible.map((p) => {
          const attention = p.checks.filter((c) => c.status === 'fail' || c.status === 'unreadable');
          const isOpen = open === p.key;
          return (
            <div key={p.key} className={`readiness-card readiness-card--${p.state}`}>
              <div className="r-top">
                <span className="r-name">{p.label}</span>
                <span className="r-badge">{STATE_LABEL[p.state]}</span>
              </div>
              <div className="r-meaning">{PHASE_MEANING[p.key]}</div>
              <div className="r-bar" aria-hidden>
                <span style={{ width: `${p.percent ?? 0}%` }} />
              </div>
              <div className="r-count">
                {p.percent === null ? 'ไม่มีข้อที่เกี่ยวกับงานนี้' : `${p.percent}% · ${p.passed}/${p.applicable} ข้อ`}
              </div>

              {attention.length === 0 ? (
                <div className="r-clear">ผ่านครบทุกข้อ</div>
              ) : (
                <>
                  <button
                    type="button"
                    className="r-toggle"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : p.key)}
                  >
                    {isOpen ? 'ซ่อนรายการ' : `ดู ${attention.length} ข้อที่ยังไม่ผ่าน`}
                  </button>
                  {isOpen && (
                    <ul className="r-list">
                      {attention.map((c) => (
                        <li key={c.id} className={c.status === 'unreadable' ? 'is-unreadable' : undefined}>
                          {c.status === 'unreadable' ? (
                            <>
                              <span className="r-label">{c.label}</span>
                              <span className="r-detail">Token นี้อ่านข้อนี้ไม่ได้</span>
                            </>
                          ) : (
                            <button type="button" onClick={() => onGoTo(c.tab)}>
                              <span className="r-label">
                                {c.blocking && <span className="r-block" title="ข้อที่กันไม่ให้เฟสนี้พร้อม">ต้องมี</span>}
                                {c.label}
                              </span>
                              <span className="r-detail">{c.detail}</span>
                              <span aria-hidden className="r-arw">→</span>
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
